// IndexedDB wrapper for offline notes storage
const DB_NAME = 'stalfes-offline-notes';
const DB_VERSION = 1;
const NOTES_STORE = 'pending-notes';
const IMAGES_STORE = 'pending-images';

export interface OfflineNote {
  id: string; // temporary ID
  clientId: string;
  content: string;
  imageUrl?: string;
  imageName?: string;
  imageSize?: number;
  imageBlob?: Blob; // stored locally
  createdAt: number; // timestamp
  synced: boolean;
  syncAttempts: number;
  lastSyncAttempt?: number;
  error?: string;
}

class OfflineNotesDB {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Store for pending notes
        if (!db.objectStoreNames.contains(NOTES_STORE)) {
          const notesStore = db.createObjectStore(NOTES_STORE, { keyPath: 'id' });
          notesStore.createIndex('synced', 'synced', { unique: false });
          notesStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Store for pending images
        if (!db.objectStoreNames.contains(IMAGES_STORE)) {
          const imagesStore = db.createObjectStore(IMAGES_STORE, { keyPath: 'id' });
          imagesStore.createIndex('noteId', 'noteId', { unique: false });
        }
      };
    });
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('Database not initialized');
    }
    return this.db;
  }

  async addNote(note: Omit<OfflineNote, 'id' | 'synced' | 'syncAttempts' | 'createdAt'>): Promise<string> {
    const db = await this.ensureDB();

    const offlineNote: OfflineNote = {
      ...note,
      id: `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: Date.now(),
      synced: false,
      syncAttempts: 0,
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([NOTES_STORE], 'readwrite');
      const store = transaction.objectStore(NOTES_STORE);
      const request = store.add(offlineNote);

      request.onsuccess = () => resolve(offlineNote.id);
      request.onerror = () => reject(request.error);
    });
  }

  async saveImage(noteId: string, blob: Blob): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([IMAGES_STORE], 'readwrite');
      const store = transaction.objectStore(IMAGES_STORE);
      const request = store.put({
        id: noteId,
        noteId,
        blob,
        timestamp: Date.now(),
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getImage(noteId: string): Promise<Blob | null> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([IMAGES_STORE], 'readonly');
      const store = transaction.objectStore(IMAGES_STORE);
      const request = store.get(noteId);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.blob : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingNotes(): Promise<OfflineNote[]> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([NOTES_STORE], 'readonly');
      const store = transaction.objectStore(NOTES_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        // Filter manually since boolean index doesn't work well with IDBKeyRange
        const allNotes = request.result || [];
        const pendingNotes = allNotes.filter(note => !note.synced);
        resolve(pendingNotes);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getAllNotes(): Promise<OfflineNote[]> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([NOTES_STORE], 'readonly');
      const store = transaction.objectStore(NOTES_STORE);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async updateNote(id: string, updates: Partial<OfflineNote>): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([NOTES_STORE], 'readwrite');
      const store = transaction.objectStore(NOTES_STORE);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const note = getRequest.result;
        if (!note) {
          reject(new Error('Note not found'));
          return;
        }

        const updatedNote = { ...note, ...updates };
        const putRequest = store.put(updatedNote);

        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async deleteNote(id: string): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([NOTES_STORE, IMAGES_STORE], 'readwrite');

      // Delete note
      const notesStore = transaction.objectStore(NOTES_STORE);
      const deleteNoteRequest = notesStore.delete(id);

      // Delete associated image
      const imagesStore = transaction.objectStore(IMAGES_STORE);
      const deleteImageRequest = imagesStore.delete(id);

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async markAsSynced(id: string): Promise<void> {
    await this.updateNote(id, { synced: true });
    // Keep synced notes for a while in case user wants to see them
    // They'll be cleaned up by periodic cleanup
  }

  async incrementSyncAttempt(id: string, error?: string): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([NOTES_STORE], 'readwrite');
      const store = transaction.objectStore(NOTES_STORE);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const note = getRequest.result;
        if (!note) {
          reject(new Error('Note not found'));
          return;
        }

        note.syncAttempts += 1;
        note.lastSyncAttempt = Date.now();
        if (error) {
          note.error = error;
        }

        const putRequest = store.put(note);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async cleanupOldSyncedNotes(olderThanHours: number = 24): Promise<number> {
    const db = await this.ensureDB();
    const cutoffTime = Date.now() - (olderThanHours * 60 * 60 * 1000);

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([NOTES_STORE, IMAGES_STORE], 'readwrite');
      const notesStore = transaction.objectStore(NOTES_STORE);
      const imagesStore = transaction.objectStore(IMAGES_STORE);
      const request = notesStore.getAll();

      request.onsuccess = () => {
        const notes = request.result || [];
        let deletedCount = 0;

        const deletePromises = notes
          .filter(note => note.synced && note.createdAt < cutoffTime)
          .map(note => {
            notesStore.delete(note.id);
            imagesStore.delete(note.id);
            deletedCount++;
          });

        transaction.oncomplete = () => resolve(deletedCount);
      };

      request.onerror = () => reject(request.error);
    });
  }
}

// Singleton instance
export const offlineNotesDB = new OfflineNotesDB();

// Helper function to check if online
export function isOnline(): boolean {
  return navigator.onLine;
}

// Helper to attempt sync
export async function syncPendingNotes(): Promise<{ success: number; failed: number }> {
  if (!isOnline()) {
    console.log('Offline - skipping sync');
    return { success: 0, failed: 0 };
  }

  const pendingNotes = await offlineNotesDB.getPendingNotes();
  console.log(`Syncing ${pendingNotes.length} pending notes`);

  let success = 0;
  let failed = 0;

  for (const note of pendingNotes) {
    try {
      // Upload image if exists
      let imageData: any = null;

      if (note.imageBlob) {
        const imageBlob = await offlineNotesDB.getImage(note.id);
        if (imageBlob) {
          const formData = new FormData();
          formData.append('file', imageBlob, note.imageName || 'image.jpg');

          const uploadResponse = await fetch('/api/notes/upload', {
            method: 'POST',
            body: formData,
          });

          if (uploadResponse.ok) {
            imageData = await uploadResponse.json();
          } else {
            throw new Error('Failed to upload image');
          }
        }
      }

      // Create note
      const response = await fetch('/api/notes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId: note.clientId,
          content: note.content,
          imageUrl: imageData?.url || note.imageUrl,
          imageName: imageData?.name || note.imageName,
          imageSize: imageData?.size || note.imageSize,
        }),
      });

      if (response.ok) {
        await offlineNotesDB.markAsSynced(note.id);
        success++;
        console.log(`✓ Synced note ${note.id}`);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        await offlineNotesDB.incrementSyncAttempt(note.id, errorData.error);
        failed++;
        console.error(`✗ Failed to sync note ${note.id}:`, errorData.error);
      }
    } catch (error) {
      await offlineNotesDB.incrementSyncAttempt(note.id, error instanceof Error ? error.message : 'Unknown error');
      failed++;
      console.error(`✗ Error syncing note ${note.id}:`, error);
    }
  }

  // Cleanup old synced notes
  await offlineNotesDB.cleanupOldSyncedNotes();

  return { success, failed };
}

// Register listeners for online/offline events
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('🟢 Back online - attempting sync');
    syncPendingNotes().then(result => {
      console.log(`Sync complete: ${result.success} succeeded, ${result.failed} failed`);
    });
  });

  window.addEventListener('offline', () => {
    console.log('🔴 Gone offline - notes will be queued');
  });
}
